import { useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Anchor, Button, Checkbox, Group, Paper, Stack, Text } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { applyCommodityPresetToCommodity } from './apply-commodity-preset';
import { CommodityDetails } from './CommodityDetails';
import { CommodityHazmatInfo } from './CommodityHazmatInfo';
import { CommodityProductAutocomplete } from './CommodityProductAutocomplete';
import { handlingUnitsStepMessages } from './handling-units-step-messages';
import styles from './WizardCommodityCard.module.css';
import { normalizeToMinorWeightUnit } from '../../../../utils/weight-utils';
import { WeightInput } from '../../../common';
import { Dropdown } from '../../../common/Dropdown';
import { FREIGHT_CLASS_OPTIONS } from '../../../freight-browser/components/freight-form/freight-class-options';
import { sliceFieldErrors } from '../../wizard-step-validation';

import type { FreightFulfillmentPlanCommodityPreset } from '../../../../types/freight-responses';
import type { FreightClass, LtlCommodity } from '../../../../types/ltl';
import type { Weight, WeightUnit } from '../../../../types/weight';

export type WizardCommodityCardProps = {
  commodity: LtlCommodity;
  index: number;
  /** The seller's preferred display weight unit, used as the fallback when the commodity's own weightUnit is missing. */
  displayWeightUnit: WeightUnit;
  onChange: (next: LtlCommodity) => void;
  onRemove?: () => void;
  isHazmatEnabled?: boolean;
  hideProductAutocomplete?: boolean;
  /** When provided, renders in place of the product autocomplete. */
  nameField?: React.ReactNode;
  /** When false, renders flat without the Paper wrapper or expand/collapse header. Defaults to true. */
  collapsible?: boolean;
  /** Called with the picked preset's id and this row's stable commodity id (not the array index, which shifts on add/remove). */
  onCommodityPresetSelect?: (presetId: number, commodityId: string) => void;
  /**
   * Formatted inline error messages keyed by field path, already sliced to this commodity's prefix
   * (e.g. `'weight'`, `'freightClass'`).
   */
  fieldErrors?: Record<string, string>;
  /** When false, weight and freight class are not marked as required. Defaults to true. */
  weightAndClassRequired?: boolean;
};

export const WizardCommodityCard = ({
  commodity,
  index,
  displayWeightUnit,
  onChange,
  onRemove,
  isHazmatEnabled = false,
  hideProductAutocomplete = false,
  nameField,
  collapsible = true,
  onCommodityPresetSelect,
  fieldErrors,
  weightAndClassRequired = true,
}: WizardCommodityCardProps) => {
  const intl = useIntl();

  const [isExpanded, setIsExpanded] = useState(true);
  const [areDetailsShown, setAreDetailsShown] = useState(true);
  const [isHazmatShown, setIsHazmatShown] = useState(commodity.isHazmat);

  // WeightInput's internal combine math assumes the passed-in unit is a minor/base unit
  // (Ounces/Grams), so always normalize into that family before handing it off.
  const originalWeightUnit = commodity.weightUnit ?? displayWeightUnit;
  const weightValue: Weight = normalizeToMinorWeightUnit({ value: commodity.weight, unit: originalWeightUnit });

  const handleProductNameChange = (value: string) => onChange({ ...commodity, productName: value });

  const handleCommodityPresetSelect = (commodityPreset: FreightFulfillmentPlanCommodityPreset) => {
    const next = applyCommodityPresetToCommodity(commodity, commodityPreset, { isHazmatEnabled });
    onChange(next);
    onCommodityPresetSelect?.(commodityPreset.commodityPresetId, commodity.id);
    // Auto-expand (never auto-collapse) sections whose fields autofill just
    // populated — otherwise required-field validation can activate on fields the
    // user can't see, or hazmat data can exist with no visible way to review it.
    if (next.isHazmat) setIsHazmatShown(true);
    if (next.description !== commodity.description || next.nmfcCode !== commodity.nmfcCode) setAreDetailsShown(true);
  };

  const handleWeightChange = (next: Weight) => onChange({ ...commodity, weight: next.value, weightUnit: next.unit });

  const handleFreightClassChange = (value: string | null) => onChange({ ...commodity, freightClass: (value as FreightClass) ?? '' });

  const fields = (
    <Stack gap='sm'>
      {nameField ??
        (!hideProductAutocomplete && (
          <CommodityProductAutocomplete
            data-testid={`commodity-product-name-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.productNameLabel)}
            onChange={handleProductNameChange}
            onCommodityPresetSelect={handleCommodityPresetSelect}
            placeholder={intl.formatMessage(handlingUnitsStepMessages.productNamePlaceholder)}
            value={commodity.productName ?? ''}
          />
        ))}

      <Group align='flex-start' grow>
        <WeightInput
          error={fieldErrors?.['weight']}
          onChange={handleWeightChange}
          showMinorUnit={false}
          value={weightValue}
          withAsterisk={weightAndClassRequired}
        />
        <Dropdown
          data={FREIGHT_CLASS_OPTIONS}
          data-testid={`commodity-class-${index}`}
          error={fieldErrors?.['freightClass']}
          label={intl.formatMessage(handlingUnitsStepMessages.classLabel)}
          onChange={handleFreightClassChange}
          placeholder={intl.formatMessage(handlingUnitsStepMessages.classPlaceholder)}
          value={commodity.freightClass || null}
          withAsterisk={weightAndClassRequired}
        />
      </Group>

      <Anchor
        className={styles.detailsToggle}
        component='button'
        data-testid={`toggle-commodity-details-${index}`}
        onClick={() => setAreDetailsShown((prev) => !prev)}
        size='sm'
        type='button'
      >
        <FormattedMessage
          {...(areDetailsShown ? handlingUnitsStepMessages.hideCommodityDetails : handlingUnitsStepMessages.showCommodityDetails)}
        />
      </Anchor>

      {areDetailsShown && <CommodityDetails commodity={commodity} index={index} onChange={onChange} />}

      {isHazmatEnabled && (
        <>
          <Checkbox
            checked={isHazmatShown}
            data-testid={`toggle-commodity-hazmat-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.hazmatCheckboxLabel)}
            onChange={() => {
              const next = !isHazmatShown;
              setIsHazmatShown(next);
              onChange({ ...commodity, isHazmat: next });
            }}
          />

          {isHazmatShown && (
            <CommodityHazmatInfo
              commodity={commodity}
              fieldErrors={fieldErrors ? sliceFieldErrors(fieldErrors, 'hazmatInfo') : undefined}
              index={index}
              onChange={onChange}
            />
          )}
        </>
      )}
    </Stack>
  );

  if (!collapsible) {
    return fields;
  }

  return (
    <Paper bg='gray.0' data-testid={`commodity-card-${index}`} p='sm' shadow='none' withBorder>
      <Stack gap='sm'>
        <Group gap='xs' justify='space-between'>
          <Anchor className={styles.commodityHeader} component='button' onClick={() => setIsExpanded((prev) => !prev)} type='button'>
            <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} size='sm' />
            <Text fw={500} size='sm'>
              <FormattedMessage {...handlingUnitsStepMessages.commodityTitle} values={{ number: index + 1 }} />
            </Text>
          </Anchor>
          {onRemove ? (
            <Button color='red' data-testid={`remove-commodity-${index}`} onClick={onRemove} size='xs' variant='ghost'>
              <FormattedMessage {...handlingUnitsStepMessages.removeCommodity} />
            </Button>
          ) : null}
        </Group>

        {isExpanded && fields}
      </Stack>
    </Paper>
  );
};
